import { useState, useEffect, useMemo } from "react";
import { Modal, Button, Space, Typography, Spin, Row, Col, message } from "antd";
import { PlusOutlined, ReloadOutlined, ArrowLeftOutlined, ConsoleSqlOutlined } from "@ant-design/icons";
import CarpetasView from "./CarpetasView";
import CarpetaFormModal from "./CarpetaFormModal";
import FiltroCarpetas from "./FiltroCarpetas";
import NavigationBreadcrumb from "./NavigationBreadcrumb"
import infraestructuraService from "../../services/infraestructura/infraestructuraService"
import { useSelector } from 'react-redux';
import { selectAppPermissions } from '../../store/auth/selectors';

const { Title, Paragraph, Text } = Typography;

export default function InfraestructuraModal({ isOpen, onClose, proyecto, estadoIniciar }) {
  const [allCarpetas, setAllCarpetas] = useState([]);
  const [currentCarpeta, setCurrentCarpeta] = useState(null);
  const [navigationPath, setNavigationPath] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formModal, setFormModal] = useState({ isOpen: false, carpeta: null, });

  ///Permisos
  const appName = 'poa-infraestructura';
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const permissionInsert = permissions.priv_insert === 'Y';
  const permissionUpdate = permissions.priv_update === 'Y';

  const currentCarpetas = useMemo(() => {
    if (!currentCarpeta) return allCarpetas;
    return currentCarpeta.subcarpetas;
  }, [allCarpetas, currentCarpeta]);

  const filteredCarpetas = useMemo(() => {
    if (!searchTerm.trim()) return currentCarpetas;
    return currentCarpetas.filter((carpeta) =>
      carpeta.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [currentCarpetas, searchTerm]);

  const loadCarpetas = async () => {
    setIsLoading(true);
    try {
      const data = await infraestructuraService.getCarpetas();
      setAllCarpetas(data);
    } catch (error) {
      console.error("Error al cargar carpetas:", error);
      message.error("Error al cargar carpetas");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadCarpetas();
      setCurrentCarpeta(null);
      setNavigationPath([]);
      setSearchTerm("");
    }
  }, [isOpen]);

  const handleNavigate = (carpeta) => {
    if (!carpeta) {
      setCurrentCarpeta(null);
      setNavigationPath([]);
    } else {
      setCurrentCarpeta(carpeta);
      const existingIndex = navigationPath.findIndex((c) => c.oid === carpeta.oid);
      if (existingIndex >= 0) {
        setNavigationPath(navigationPath.slice(0, existingIndex + 1));
      } else {
        setNavigationPath([...navigationPath, carpeta]);
      }
    }
    setSearchTerm("");
  };

  const handleCreateNew = () => {
    setFormModal({ isOpen: true, carpeta: null });
  };

  const handleEdit = (carpeta) => {
    setFormModal({ isOpen: true, carpeta });
  };

  const handleFormClose = () => {
    setFormModal({ isOpen: false, carpeta: null });
  };

  const findNodeByOid = (nodes, oid) => {
    for (const node of nodes) {
      if (node.oid === oid) return node;
      if (node.subcarpetas) {
        const found = findNodeByOid(node.subcarpetas, oid);
        if (found) return found;
      }
    }
    return null;
  };


  const handleFormSuccess = (newOrUpdatedCarpeta) => {
    const updatedNode = newOrUpdatedCarpeta.data;
    setAllCarpetas((prev) => {
      let updatedTree;

      if (newOrUpdatedCarpeta.isEditing) {
        updatedTree = updateTreeNode(prev, updatedNode);
        if (currentCarpeta?.oid) {
          const refreshedCurrent = findNodeByOid(updatedTree, currentCarpeta.oid);
          setCurrentCarpeta(refreshedCurrent);
        }
        setNavigationPath((prevPath) =>
          prevPath.map((node) =>
            node.oid === updatedNode.oid ? { ...node, ...updatedNode } : node
          )
        );
      } else {
        if (currentCarpeta?.oid) {
          updatedTree = addTreeNode(prev, currentCarpeta.oid, updatedNode);
          const refreshedCurrent = findNodeByOid(updatedTree, currentCarpeta.oid);
          setCurrentCarpeta(refreshedCurrent);
        } else {
          updatedTree = [...prev, updatedNode];
        }
      }

      return updatedTree;
    });
  };

  const handleRefresh = () => {
    loadCarpetas();
  };

  const handleGoBack = () => {
    if (navigationPath.length > 1) {
      const newPath = navigationPath.slice(0, -1);
      setNavigationPath(newPath);
      setCurrentCarpeta(newPath[newPath.length - 1]);
    } else {
      setCurrentCarpeta(null);
      setNavigationPath([]);
    }
  };

  const updateTreeNode = (nodes, updatedNode) => {
    return nodes.map((node) => {
      if (node.oid === updatedNode.oid) {
        return { ...node, ...updatedNode }; // reemplaza con datos nuevos
      }
      if (node.subcarpetas) {
        return { ...node, subcarpetas: updateTreeNode(node.subcarpetas, updatedNode) };
      }
      return node;
    });
  };

  const addTreeNode = (nodes, parentOid, newNode) => {
    return nodes.map((node) => {
      if (node.oid === parentOid) {
        return {
          ...node,
          subcarpetas: [...(node.subcarpetas || []), newNode]
        };
      }
      if (node.subcarpetas) {
        return { ...node, subcarpetas: addTreeNode(node.subcarpetas, parentOid, newNode) };
      }
      return node;
    });
  };

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={onClose}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        styles={{ body: { padding: 24, maxHeight: "80vh", overflowY: "auto" } }}
        zIndex={2000}
      >
        <Title level={3}>Avance Infraestructura</Title>
        <div className="border-b pb-3 overflow-x-auto">
          <NavigationBreadcrumb currentPath={navigationPath} onNavigate={handleNavigate} />
        </div>

        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row gutter={[8, 8]} align="middle" justify="middle">
            {currentCarpeta && (
              <Col flex="none">
                <Button icon={<ArrowLeftOutlined />} onClick={handleGoBack}>
                  Atrás
                </Button>
              </Col>
            )}
            <Col flex="auto" style={{ maxWidth: 400 }}>
              <FiltroCarpetas searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </Col>
            <Col flex="none">
              <Space>
                <Button
                  icon={<ReloadOutlined spin={isLoading} />}
                  onClick={handleRefresh}
                  disabled={isLoading}
                >
                  Actualizar
                </Button>
                {permissionInsert && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateNew}
                  >
                    {currentCarpeta ? "Nueva Subcarpeta" : "Nueva Carpeta"}
                  </Button>
                )}

              </Space>
            </Col>
          </Row>
          <Spin spinning={isLoading}>
            {
              <CarpetasView
                carpetas={filteredCarpetas}
                onEdit={handleEdit}
                onRefresh={handleRefresh}
                onNavigate={handleNavigate}
                isLoading={isLoading}
                proyecto={proyecto}
                permissionUpdate={permissionUpdate}
                estadoIniciar={estadoIniciar}
              />
            }
          </Spin>
          {searchTerm && (
            <Text type="secondary">
              Mostrando {filteredCarpetas.length} de {currentCarpetas.length} elementos
            </Text>
          )}
        </Space>
      </Modal>
      {
        <CarpetaFormModal
          isOpen={formModal.isOpen}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          carpeta={formModal.carpeta}
          carpetas={allCarpetas}
          currentParent={currentCarpeta}
        />
      }
    </>
  );
}
